import { useRef } from "react";
import { Bold, Italic, List, ListOrdered } from "lucide-react";

const BLOCK_TAGS = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "TR", "SECTION", "ARTICLE"]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "META", "TITLE", "HEAD", "LINK", "NOSCRIPT"]);

function isBoldEl(el) {
  const fw = (el.style && el.style.fontWeight) || "";
  if (fw) return fw === "bold" || fw === "bolder" || parseInt(fw, 10) >= 600;
  return el.tagName === "B" || el.tagName === "STRONG" || /^H[1-6]$/.test(el.tagName);
}

function isItalicEl(el) {
  const fs = (el.style && el.style.fontStyle) || "";
  if (fs) return fs === "italic" || fs === "oblique";
  return el.tagName === "I" || el.tagName === "EM";
}

function wrapLines(text, mark) {
  return text
    .split("\n")
    .map((line) => {
      const m = /^((?:- |\d+\. )?)(\s*)(.*?)(\s*)$/.exec(line);
      if (!m[3]) return line;
      return m[1] + m[2] + mark + m[3] + mark + m[4];
    })
    .join("\n");
}

function renderNode(node, ctx) {
  if (node.nodeType === 3) return node.nodeValue.replace(/\s+/g, " ");
  if (node.nodeType !== 1) return "";
  const tag = node.tagName;
  if (SKIP_TAGS.has(tag)) return "";
  if (tag === "BR") return "\n";

  if (tag === "UL" || tag === "OL") {
    const items = [];
    let n = 0;
    node.childNodes.forEach((li) => {
      if (li.nodeType !== 1 || li.tagName !== "LI") return;
      n += 1;
      let own = "";
      let nested = "";
      li.childNodes.forEach((c) => {
        if (c.nodeType === 1 && (c.tagName === "UL" || c.tagName === "OL")) nested += renderNode(c, ctx);
        else own += renderNode(c, ctx);
      });
      own = own.replace(/\s+/g, " ").trim();
      if (own) items.push((tag === "OL" ? `${n}. ` : "- ") + own);
      if (nested.trim()) items.push(nested.trim());
    });
    return items.length ? `\n\n${items.join("\n")}\n\n` : "";
  }

  const bold = isBoldEl(node);
  const italic = isItalicEl(node);
  const childCtx = { bold: ctx.bold || bold, italic: ctx.italic || italic };
  let inner = "";
  node.childNodes.forEach((c) => { inner += renderNode(c, childCtx); });

  if (bold && !ctx.bold) inner = wrapLines(inner, "**");
  else if (italic && !ctx.italic && !ctx.bold && !inner.includes("*")) inner = wrapLines(inner, "*");

  if (BLOCK_TAGS.has(tag)) return `\n\n${inner.trim()}\n\n`;
  if (tag === "TD" || tag === "TH") return `${inner} `;
  return inner;
}

// Converts pasted HTML into the editor's markdown-lite text. Runs fully in the browser.
function htmlToMarkdownLite(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let out = "";
  doc.body.childNodes.forEach((n) => { out += renderNode(n, { bold: false, italic: false }); });
  return out
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function wrapSelection(textarea, before, after, placeholder) {
  const { selectionStart, selectionEnd, value } = textarea;
  const selected = value.slice(selectionStart, selectionEnd) || placeholder;
  const newValue = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd);
  const cursorStart = selectionStart + before.length;
  const cursorEnd = cursorStart + selected.length;
  return { newValue, cursorStart, cursorEnd };
}

function prefixLines(textarea, makePrefix) {
  const { selectionStart, selectionEnd, value } = textarea;
  const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
  let lineEnd = value.indexOf("\n", selectionEnd);
  if (lineEnd === -1) lineEnd = value.length;
  const block = value.slice(lineStart, lineEnd);
  const lines = block.length ? block.split("\n") : [""];
  const prefixed = lines.map((line, i) => `${makePrefix(i)}${line}`).join("\n");
  const newValue = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
  return { newValue, cursorStart: lineStart, cursorEnd: lineStart + prefixed.length };
}

/**
 * RichTextEditor
 * A dependency-free "markdown-lite" textarea: toolbar buttons insert
 * **bold**, *italic*, "- " bullets and "1. " numbered-list syntax at
 * the cursor/selection. Paired with <RichText> for rendering — see
 * that file for the (safe, non-HTML) parser.
 */
export default function RichTextEditor({ value, onChange, rows = 4, maxLength, placeholder, className = "" }) {
  const ref = useRef(null);

  const applyEdit = (editFn) => {
    const textarea = ref.current;
    if (!textarea) return;
    const { newValue, cursorStart, cursorEnd } = editFn(textarea);
    const clipped = maxLength ? newValue.slice(0, maxLength) : newValue;
    onChange(clipped);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(Math.min(cursorStart, clipped.length), Math.min(cursorEnd, clipped.length));
    });
  };

  const handlePaste = (e) => {
    const html = e.clipboardData.getData("text/html");
    // Plain-text pastes (or HTML with no formatting) keep the browser's normal behaviour
    if (!html || !/<(b|strong|i|em|ul|ol|li|h[1-6])[\s>]|font-weight|font-style/i.test(html)) return;
    const md = htmlToMarkdownLite(html);
    if (!md) return;
    e.preventDefault();
    applyEdit((t) => {
      const { selectionStart, selectionEnd, value: current } = t;
      const newValue = current.slice(0, selectionStart) + md + current.slice(selectionEnd);
      const pos = selectionStart + md.length;
      return { newValue, cursorStart: pos, cursorEnd: pos };
    });
  };

  const btnClass = "flex h-7 w-7 items-center justify-center rounded-lg text-ink/50 hover:bg-ink/5 hover:text-ink";

  return (
    <div className={`w-full overflow-hidden rounded-xl border border-ink/15 focus-within:border-hotpink ${className}`}>
      <div className="flex items-center gap-0.5 border-b border-ink/10 bg-ink/[0.03] px-1.5 py-1">
        <button type="button" onClick={() => applyEdit((t) => wrapSelection(t, "**", "**", "bold text"))} className={btnClass} title="Bold">
          <Bold size={14} />
        </button>
        <button type="button" onClick={() => applyEdit((t) => wrapSelection(t, "*", "*", "italic text"))} className={btnClass} title="Italic">
          <Italic size={14} />
        </button>
        <button type="button" onClick={() => applyEdit((t) => prefixLines(t, () => "- "))} className={btnClass} title="Bullet list">
          <List size={14} />
        </button>
        <button type="button" onClick={() => applyEdit((t) => prefixLines(t, (i) => `${i + 1}. `))} className={btnClass} title="Numbered list">
          <ListOrdered size={14} />
        </button>
      </div>
      <textarea
        ref={ref}
        rows={rows}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={handlePaste}
        placeholder={placeholder}
        className="w-full resize-none border-0 px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}